import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, X, Globe, CheckCircle2, AlertCircle, Star } from 'lucide-react';
import { AdminService } from '../../services/api';

const EMPTY_FORM = {
  id: null,
  currency_code: '',
  currency_symbol: '',
  currency_name: '',
  is_active: 1
};

export default function CurrencyMaster({ showCustomAlert }) {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const showAlert = (type, message) => {
    if (showCustomAlert) {
      showCustomAlert(message);
    } else {
      setAlert({ show: true, type, message });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
    }
  };

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const res = await AdminService.getCurrencies();
      if (res.status === 1) {
        setCurrencies(res.data || []);
      }
    } catch (err) {
      showAlert('error', `Failed to load currencies: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const handleOpenAdd = () => {
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setFormData({
      id: item.id,
      currency_code: item.currency_code || '',
      currency_symbol: item.currency_symbol || '',
      currency_name: item.currency_name || '',
      is_active: item.is_active !== undefined ? item.is_active : 1
    });
    setModalOpen(true);
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete currency "${code}"? This action cannot be undone.`)) return;
    try {
      const res = await AdminService.deleteCurrency(id);
      if (res.status === 1) {
        showAlert('success', `Currency ${code} deleted successfully`);
        fetchCurrencies();
      } else {
        showAlert('error', res.msg || 'Failed to delete currency');
      }
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.currency_code.trim()) {
      showAlert('error', 'Currency code (e.g. USD, AED) is required');
      return;
    }
    if (!formData.currency_symbol.trim()) {
      showAlert('error', 'Currency symbol (e.g. $, AED) is required');
      return;
    }
    if (!formData.currency_name.trim()) {
      showAlert('error', 'Currency name is required');
      return;
    }

    setSaving(true);
    try {
      const res = await AdminService.saveCurrency({
        ...formData,
        currency_code: formData.currency_code.trim().toUpperCase(),
      });
      if (res.status === 1) {
        showAlert('success', 'Currency saved successfully');
        setModalOpen(false);
        fetchCurrencies();
      } else {
        showAlert('error', res.msg || 'Failed to save currency');
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = currencies.filter(c => {
    const q = searchTerm.toLowerCase();
    return (
      (c.currency_code && c.currency_code.toLowerCase().includes(q)) ||
      (c.currency_name && c.currency_name.toLowerCase().includes(q)) ||
      (c.currency_symbol && c.currency_symbol.toLowerCase().includes(q))
    );
  });

  return (
    <div className="leads-management-wrapper" style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {alert.show && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          background: alert.type === 'success' ? '#ecfdf5' : '#fef2f2',
          color: alert.type === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${alert.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          fontSize: '0.85rem'
        }}>
          {alert.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{alert.message}</span>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Globe size={24} color="#2563eb" />
            Global Currency Master
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
            Manage operating currencies and symbols for platform subscription plans and healthcare billing.
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            padding: '0.6rem 1.1rem',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(37,99,235,0.2)'
          }}
        >
          <Plus size={16} /> Add Currency
        </button>
      </div>

      {/* SEARCH BAR */}
      <div style={{ marginBottom: '1rem', position: 'relative', maxWidth: '360px' }}>
        <Search size={16} style={{ position: 'absolute', left: '12px', top: '10px', color: '#94a3b8' }} />
        <input
          type="text"
          placeholder="Search by code, symbol, or name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.55rem 0.75rem 0.55rem 2.2rem',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            fontSize: '0.85rem',
            outline: 'none',
            background: '#fff'
          }}
        />
      </div>

      {/* TABLE */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
              <th style={{ padding: '0.75rem 1rem' }}>Code</th>
              <th style={{ padding: '0.75rem 1rem' }}>Symbol</th>
              <th style={{ padding: '0.75rem 1rem' }}>Currency Name</th>
              <th style={{ padding: '0.75rem 1rem' }}>Status</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                  Loading currencies...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                  No currencies found.
                </td>
              </tr>
            ) : (
              filtered.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <code style={{ background: '#eff6ff', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                      {c.currency_code}
                    </code>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{c.currency_symbol}</strong>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#1e293b', fontWeight: 500 }}>
                    {c.currency_name}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: c.is_active ? '#059669' : '#dc2626'
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.is_active ? '#059669' : '#dc2626' }}></span>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleOpenEdit(c)}
                        style={{ background: 'none', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.75rem' }}
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(c.id, c.currency_code)}
                        style={{ background: 'none', border: '1px solid #fca5a5', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.75rem' }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {modalOpen && (
        <div className="lead-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="lead-modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
                <Globe size={18} color="#2563eb" />
                {formData.id ? 'Edit Currency' : 'Add New Currency'}
              </h3>
              <button className="close-btn" onClick={() => setModalOpen(false)}><X size={18} /></button>
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>
                      Currency Code (ISO) *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. USD, AED"
                      maxLength={10}
                      value={formData.currency_code}
                      onChange={e => setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })}
                      style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', textTransform: 'uppercase' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>
                      Currency Symbol *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. $, AED, KD"
                      maxLength={10}
                      value={formData.currency_symbol}
                      onChange={e => setFormData({ ...formData, currency_symbol: e.target.value })}
                      style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>
                    Currency Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. US Dollar, UAE Dirham"
                    value={formData.currency_name}
                    onChange={e => setFormData({ ...formData, currency_name: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                </div>

                <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600, color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={!!formData.is_active}
                      onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                    />
                    Active for Billing & Subscriptions
                  </label>
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  style={{ background: '#f1f5f9', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ background: '#2563eb', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '6px', fontSize: '0.85rem', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  {saving ? 'Saving...' : 'Save Currency'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
